import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { 
  Brain, 
  BarChart3, 
  Activity, 
  TrendingUp, 
  Users, 
  Clock,
  Target,
  AlertTriangle,
  Database,
  Cpu,
  LineChart
} from 'lucide-react';

export default function Methodology() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center gap-3 mb-4">
              <Brain className="w-10 h-10 text-purple-400" />
              <div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">
                  Our Methodology
                </h1>
                <p className="text-muted-foreground">
                  How we generate predictions and probability estimates
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="prose prose-invert max-w-none space-y-8">
            
            {/* Overview */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                Overview
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  SportsAnalysisAB uses a <strong className="text-foreground">unified prediction model</strong> that 
                  combines traditional statistical analysis with advanced AI-powered insights. Our goal is to provide 
                  the most accurate probability estimates possible while maintaining full transparency about our methods.
                </p>
              </div>
            </section>

            {/* Two-Phase Approach */}
            <section className="bg-card border border-border rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-purple-400" />
                Our Two-Phase Prediction Model
              </h2>
              <div className="text-muted-foreground space-y-6 text-sm">
                <div className="bg-secondary/30 rounded-sm p-4">
                  <h3 className="font-bold text-blue-400 mb-2 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Phase 1: Statistical Baseline
                  </h3>
                  <p>
                    We establish a foundation using historical performance data:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                    <li>Team win/loss records and winning percentages</li>
                    <li>Home vs. away performance differentials</li>
                    <li>Recent form (last 5-10 games)</li>
                    <li>Head-to-head historical matchups</li>
                    <li>League-specific home advantage factors</li>
                  </ul>
                </div>
                
                <div className="bg-secondary/30 rounded-sm p-4">
                  <h3 className="font-bold text-green-400 mb-2 flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    Phase 2: AI Enhancement
                  </h3>
                  <p>
                    We enhance our baseline with AI-powered analysis:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                    <li>Injury impact assessment (key player absences)</li>
                    <li>Lineup and roster changes</li>
                    <li>Schedule fatigue (back-to-backs, rest days)</li>
                    <li>Contextual factors (playoff implications, rivalries)</li>
                    <li>Trend analysis and momentum indicators</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Sources */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-400" />
                Data Sources
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>Our predictions are powered by data from reliable sources:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">Live Game Data:</strong> Real-time scores, schedules, and status updates</li>
                  <li><strong className="text-foreground">Team Statistics:</strong> Season records, home/away splits, divisional performance</li>
                  <li><strong className="text-foreground">Injury Reports:</strong> Player availability and injury status</li>
                  <li><strong className="text-foreground">Historical Results:</strong> Past game outcomes for model training</li>
                  <li><strong className="text-foreground">Standings:</strong> Current league standings and playoff positions</li>
                </ul>
                <p className="mt-4 text-xs text-muted-foreground">
                  Data is refreshed automatically throughout the day to ensure accuracy.
                </p>
              </div>
            </section>

            {/* Key Factors */}
            <section className="bg-card border border-border rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-orange-400" />
                Key Prediction Factors
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-secondary/30 rounded-sm p-4">
                  <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-blue-400" />
                    Home Field Advantage
                  </h3>
                  <p className="text-muted-foreground text-xs">
                    We apply league-specific home advantage adjustments:
                  </p>
                  <ul className="text-muted-foreground text-xs mt-2 space-y-1">
                    <li>NBA: ~5% probability boost</li>
                    <li>NFL: ~4% probability boost</li>
                    <li>NHL: ~3% probability boost</li>
                    <li>MLS: ~3% probability boost</li>
                    <li>MLB: ~2% probability boost</li>
                  </ul>
                </div>
                
                <div className="bg-secondary/30 rounded-sm p-4">
                  <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4 text-red-400" />
                    Injury Impact
                  </h3>
                  <p className="text-muted-foreground text-xs">
                    Key player absences can significantly affect outcomes. We analyze:
                  </p>
                  <ul className="text-muted-foreground text-xs mt-2 space-y-1">
                    <li>Star player availability</li>
                    <li>Position importance</li>
                    <li>Depth chart adjustments</li>
                    <li>Historical performance without key players</li>
                  </ul>
                </div>
                
                <div className="bg-secondary/30 rounded-sm p-4">
                  <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    Recent Form
                  </h3>
                  <p className="text-muted-foreground text-xs">
                    Current momentum and form are crucial indicators:
                  </p>
                  <ul className="text-muted-foreground text-xs mt-2 space-y-1">
                    <li>Last 5-10 game performance</li>
                    <li>Winning/losing streaks</li>
                    <li>Point differential trends</li>
                    <li>Performance vs. quality opponents</li>
                  </ul>
                </div>
                
                <div className="bg-secondary/30 rounded-sm p-4">
                  <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-yellow-400" />
                    Schedule Fatigue
                  </h3>
                  <p className="text-muted-foreground text-xs">
                    Rest and travel can impact performance:
                  </p>
                  <ul className="text-muted-foreground text-xs mt-2 space-y-1">
                    <li>Days of rest between games</li>
                    <li>Back-to-back game situations</li>
                    <li>Travel distance and time zones</li>
                    <li>Games in last 7 days</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Confidence Levels */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <LineChart className="w-5 h-5 text-green-400" />
                Confidence Levels Explained
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>We assign confidence levels based on data quality and prediction certainty:</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-green-500/10 border border-green-500/20 rounded-sm p-4 text-center">
                    <span className="font-bold text-green-400 text-lg">HIGH</span>
                    <p className="text-xs text-muted-foreground mt-2">
                      Strong statistical indicators, comprehensive data, clear favorites.
                      Probability spread typically 60%+ to one side.
                    </p>
                  </div>
                  
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-sm p-4 text-center">
                    <span className="font-bold text-yellow-400 text-lg">MEDIUM</span>
                    <p className="text-xs text-muted-foreground mt-2">
                      Mixed signals, competitive matchup, some uncertainty.
                      Probability spread typically 52-60%.
                    </p>
                  </div>
                  
                  <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-center">
                    <span className="font-bold text-red-400 text-lg">LOW</span>
                    <p className="text-xs text-muted-foreground mt-2">
                      Limited data, evenly matched teams, high uncertainty.
                      Probability spread below 52% (near coin-flip).
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Pre-Game Updates */}
            <section className="bg-blue-500/10 border border-blue-500/20 rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-blue-400 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Pre-Game Prediction Updates
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  Our predictions are not static. We automatically update them as new information becomes available:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-foreground">~2 Hours Before Game:</strong> Final prediction update with latest injury reports and lineup confirmations</li>
                  <li><strong className="text-foreground">At Game Time:</strong> Prediction is locked and no further changes are made</li>
                </ul>
                <p className="mt-4">
                  This ensures our predictions reflect the most current information while maintaining 
                  integrity by locking predictions before games start.
                </p>
              </div>
            </section>

            {/* Score Predictions */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                Score Predictions
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  In addition to win probabilities, we provide predicted final scores. These are based on:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>League average scoring patterns</li>
                  <li>Team offensive and defensive ratings</li>
                  <li>Head-to-head scoring history</li>
                  <li>Home/away scoring adjustments</li>
                </ul>
                <p className="mt-4">
                  <strong className="text-foreground">For Soccer (MLS):</strong> Ties are predicted when our model 
                  indicates a very close match with high likelihood of a draw (exact score equality).
                </p>
              </div>
            </section>

            {/* Accuracy Tracking */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-green-400" />
                Accuracy Tracking
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  We maintain complete transparency about our prediction performance:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Every prediction is logged and tracked</li>
                  <li>Results are automatically recorded when games complete</li>
                  <li>Accuracy statistics are updated in real-time</li>
                  <li>We display accuracy by league, confidence level, and time period</li>
                </ul>
                <p className="mt-4">
                  Visit our <a href="/accuracy" className="text-blue-400 hover:underline">Accuracy Report</a> page 
                  to see our current prediction performance statistics.
                </p>
              </div>
            </section>

            {/* Disclaimer */}
            <section className="bg-yellow-500/10 border border-yellow-500/20 rounded-sm p-6">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-yellow-400 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Important Disclaimer
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  Our predictions are generated using statistical models and historical data. 
                  While we strive for accuracy:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Sports outcomes are inherently unpredictable</li>
                  <li>No model can account for all variables</li>
                  <li>Past performance does not guarantee future results</li>
                  <li>Unexpected events can significantly impact outcomes</li>
                </ul>
                <p className="mt-4 font-bold text-foreground">
                  Our content is for entertainment and educational purposes only. 
                  We do not provide betting advice.
                </p>
              </div>
            </section>

            {/* Continuous Improvement */}
            <section>
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground mb-4">
                Continuous Improvement
              </h2>
              <div className="text-muted-foreground space-y-4 text-sm">
                <p>
                  We are constantly working to improve our prediction accuracy:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Analyzing prediction errors to identify patterns</li>
                  <li>Incorporating new data sources and factors</li>
                  <li>Refining our AI models based on results</li>
                  <li>Adjusting league-specific parameters</li>
                </ul>
                <p className="mt-4">
                  Our goal is to provide the most accurate and useful sports analytics 
                  available while maintaining complete transparency about our methods and results.
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
